import classNames from "classnames";
import React, { FC, useCallback, useState } from "react";
import { usePopper } from "react-popper";
import { Button } from "./Button";
import { StreamLineIcon } from "../../SvgIcon/StreamLineIcon";

type TooltipPosition =
  | "top-start"
  | "top"
  | "top-end"
  | "right-start"
  | "right"
  | "right-end"
  | "bottom-start"
  | "bottom"
  | "bottom-end"
  | "left-start"
  | "left"
  | "left-end";

interface TooltipProps {
  text: string;
  placement: TooltipPosition;
  anchorClassName?: string;
  tooltipClassName?: string;
}

export const Tooltip: FC<TooltipProps> = (props) => {
  const { text, placement, anchorClassName, tooltipClassName } = props;

  const [isPopperOpen, setIsPopperOpen] = useState(false);

  const [anchorElement, setAnchorElement] =
    useState<HTMLButtonElement | null>();
  const [tooltipElement, setTooltipElement] = useState<HTMLDivElement | null>();
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>();

  const offsetCallback: any = useCallback(({ placement }: any) => {
    if (placement.endsWith("-end")) {
      return [16, 0];
    }

    if (placement.endsWith("-start")) {
      return [-16, 0];
    }

    return [0, 0];
  }, []);

  const { styles, attributes } = usePopper(anchorElement, tooltipElement, {
    modifiers: [
      { name: "arrow", options: { element: arrowElement } },
      { name: "offset", options: { offset: offsetCallback } },
    ],
    placement,
  });

  const handlePopperOpen = () => setIsPopperOpen(true);
  const handlePopperClose = () => setIsPopperOpen(false);

  const arrowPositionModifiers = {
    "Tooltip--top": placement.includes("top"),
    "Tooltip--bottom": placement.includes("bottom"),
    "Tooltip--left": placement.includes("left"),
    "Tooltip--right": placement.includes("right"),
  };

  return (
    <>
      <Button
        className={classNames("Tooltip__anchor", anchorClassName)}
        ref={setAnchorElement}
        onMouseEnter={handlePopperOpen}
        onMouseLeave={handlePopperClose}
        onFocus={handlePopperOpen}
        onBlur={handlePopperClose}
        type="clear"
        tier="primary"
        size="small"
      >
        <StreamLineIcon
          className="Tooltip__anchorIcon"
          iconName="s-info-circle"
        />
      </Button>
      <div
        role="tooltip"
        className={classNames("Tooltip", {
          "Tooltip--hidden": !isPopperOpen,
          ...arrowPositionModifiers,
        })}
        ref={setTooltipElement}
        style={styles.popper}
        {...attributes.popper}
      >
        <div className={classNames("Tooltip__text", tooltipClassName)}>
          {text}
        </div>
        <div
          className={"Tooltip__arrow"}
          ref={setArrowElement}
          style={styles.arrow}
        />
      </div>
    </>
  );
};
